import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { clientPhotosActions } from '../../_actions/clientPhotos.actions';
import { FIXED_TAG_SET } from '../../_constants/clientPhotos.constants';
import ClientPhotoLightbox from './ClientPhotoLightbox';
import ClientPhotoConsentModal from './ClientPhotoConsentModal';
import './ClientPhotosTab.css';

const PLUS_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const CAMERA_ICON = (
    <svg width="44" height="44" viewBox="0 0 64 64" fill="none">
        <rect x="6" y="16" width="52" height="38" rx="6" stroke="#29282D" strokeWidth="2" />
        <path d="M22 16l3-6h14l3 6" stroke="#29282D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="32" cy="35" r="10" stroke="#29282D" strokeWidth="2" />
        <circle cx="32" cy="35" r="4" fill="#007EA0" />
        <path d="M48 22l2-1" stroke="#007EA0" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const SPARKLE_ICON = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" fill="#007EA0" />
    </svg>
);

const groupByMonth = (items) => {
    const groups = [];
    let lastKey = null;
    items.forEach((item) => {
        const m = moment(item.capturedAt);
        const key = m.format('YYYY-MM');
        const label = m.isSame(moment(), 'month')
            ? 'This month'
            : m.isSame(moment().subtract(1, 'month'), 'month')
                ? 'Last month'
                : m.format('MMMM YYYY');
        if (key !== lastKey) {
            groups.push({ key, label, items: [item] });
            lastKey = key;
        } else {
            groups[groups.length - 1].items.push(item);
        }
    });
    return groups;
};

const ClientPhotosTab = ({ clientId, clientName }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const state = useSelector(
        (s) => s.clientPhotos?.byClientId?.[clientId] || null,
    );
    const fileInputRef = useRef(null);
    const sentinelRef = useRef(null);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [consentModalPending, setConsentModalPending] = useState(null);
    const undoTimeoutsRef = useRef({});
    const [visibleUndos, setVisibleUndos] = useState([]);

    // Initial load when tab opens — photos + salon-wide tag list for picker.
    useEffect(() => {
        if (!clientId) return;
        dispatch(clientPhotosActions.fetchPhotos(clientId));
        dispatch(clientPhotosActions.fetchSalonTags());
    }, [clientId, dispatch]);

    // Intersection observer for infinite scroll pagination
    useEffect(() => {
        const target = sentinelRef.current;
        if (!target || !state?.nextCursor || state.loadingMore) return undefined;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    dispatch(clientPhotosActions.loadMore(clientId));
                }
            });
        }, { rootMargin: '200px' });
        observer.observe(target);
        return () => observer.disconnect();
    }, [clientId, dispatch, state?.nextCursor, state?.loadingMore]);

    // Commit undoable deletes after 5s
    const scheduleDeleteCommit = useCallback((photoId) => {
        setVisibleUndos((prev) => [...prev.filter((u) => u.photoId !== photoId), { photoId, at: Date.now() }]);
        const timer = setTimeout(() => {
            dispatch(clientPhotosActions.commitDelete(clientId, photoId));
            setVisibleUndos((prev) => prev.filter((u) => u.photoId !== photoId));
            delete undoTimeoutsRef.current[photoId];
        }, 5000);
        undoTimeoutsRef.current[photoId] = timer;
    }, [clientId, dispatch]);

    // Leaving mid-undo still deletes: commit pending deletes instead of
    // dropping them (a cleared timer left the photo on the server).
    useEffect(() => {
        const flush = () => {
            Object.entries(undoTimeoutsRef.current).forEach(([photoId, timer]) => {
                clearTimeout(timer);
                dispatch(clientPhotosActions.commitDelete(clientId, photoId));
            });
            undoTimeoutsRef.current = {};
        };
        window.addEventListener('pagehide', flush);
        return () => { window.removeEventListener('pagehide', flush); flush(); };
    }, [clientId, dispatch]);

    const items = state?.items || [];
    const pendingUploads = state?.pendingUploads || [];
    const loading = state?.loading;
    const hasConsent = !!state?.consent?.givenAt;
    const groups = useMemo(() => groupByMonth(items), [items]);
    const hasAny = items.length > 0 || pendingUploads.length > 0;

    const runUploads = (files) => {
        Array.from(files).forEach((file) => {
            dispatch(clientPhotosActions.uploadPhoto(clientId, file));
        });
    };

    const handleAddClick = () => {
        if (!hasConsent) {
            setConsentModalPending('pick');
            return;
        }
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFilesPicked = (e) => {
        const files = e.target.files;
        if (!files || !files.length) return;
        runUploads(files);
        e.target.value = '';
    };

    const handleConsentConfirmed = async () => {
        await dispatch(clientPhotosActions.setConsent(clientId));
        const action = consentModalPending;
        setConsentModalPending(null);
        if (action === 'pick') {
            setTimeout(() => {
                if (fileInputRef.current) fileInputRef.current.click();
            }, 50);
        }
    };

    const handleUndo = (photoId) => {
        clearTimeout(undoTimeoutsRef.current[photoId]);
        delete undoTimeoutsRef.current[photoId];
        dispatch(clientPhotosActions.undoDelete(clientId, photoId));
        setVisibleUndos((prev) => prev.filter((u) => u.photoId !== photoId));
    };

    const handleDeleteFromLightbox = (photoId) => {
        dispatch(clientPhotosActions.deletePhoto(clientId, photoId));
        scheduleDeleteCommit(photoId);
    };

    const handleShareConsent = (photoId, value, previous) => {
        dispatch(clientPhotosActions.setShareConsent(clientId, photoId, value, previous));
    };

    const handleRetag = (photoId, tags, previousTags) => {
        dispatch(clientPhotosActions.patchPhotoTags(clientId, photoId, tags, previousTags));
    };

    // Jump straight into the diary for the photo's linked booking. Closes
    // the lightbox + any parent slide-outs by navigating to the diary
    // route and firing the universal `beu-open-booking` event DiaryV2
    // already listens for, so the handler runs whether we're mounting
    // fresh or already on the diary.
    const handleViewBooking = (photo) => {
        if (!photo?.bookingId) return;
        setLightboxIndex(null);
        const salonId = localStorage.getItem('salonId') || '';
        const fire = () => {
            document.dispatchEvent(new CustomEvent('beu-open-booking', {
                detail: { bookingId: photo.bookingId, date: photo.bookingDate },
            }));
        };
        // If we're already on /diary/:salonId just fire the event; if
        // not, navigate there first and dispatch after mount settles.
        if (history.location.pathname.startsWith('/diary/')) {
            setTimeout(fire, 160);
        } else {
            history.push(`/diary/${salonId}`);
            setTimeout(fire, 400);
        }
    };

    const handleRevokeConsent = () => {
        // eslint-disable-next-line no-restricted-globals, no-alert
        const ok = confirm(`Remove photo consent for ${clientName || 'this client'}? This removes every photo on their record.`);
        if (!ok) return;
        dispatch(clientPhotosActions.revokeConsent(clientId));
    };

    const renderEmpty = () => (
        <div className="cpt-empty">
            <div className="cpt-empty-art">{CAMERA_ICON}</div>
            <h3 className="cpt-empty-title">
                Build a visual record for {clientName?.split(' ')[0] || 'this client'}
            </h3>
            <p className="cpt-empty-sub">
                Capture before, after, and reference shots so you always remember what worked.
            </p>
            <button type="button" className="cpt-empty-cta" onClick={handleAddClick}>
                {SPARKLE_ICON}
                <span>Add first photo</span>
            </button>
            <div className="cpt-empty-ghosts" aria-hidden>
                <span className="cpt-ghost" />
                <span className="cpt-ghost" />
                <span className="cpt-ghost" />
            </div>
        </div>
    );

    const renderTimeline = () => (
        <div className="cpt-timeline">
            {groups.map((g) => (
                <div className="cpt-group" key={g.key}>
                    <div className="cpt-group-label">{g.label}</div>
                    <div className="cpt-row">
                        {g.items.map((photo) => {
                            const idx = items.indexOf(photo);
                            const tags = photo.tags || [];
                            const visible = tags.slice(0, 2);
                            const hidden = tags.length - visible.length;
                            return (
                                <button
                                    key={photo.id}
                                    type="button"
                                    className="cpt-tile"
                                    onClick={() => setLightboxIndex(idx)}
                                    aria-label={`Photo from ${moment(photo.capturedAt).format('D MMMM YYYY')}`}
                                >
                                    <img src={photo.thumbUrl || photo.url} alt="" loading="lazy" />
                                    {tags.length > 0 && (
                                        <div className="cpt-tag-row">
                                            {visible.map((t) => (
                                                <span
                                                    key={t}
                                                    className={`cpt-tag ${FIXED_TAG_SET.has(t) ? `cpt-tag--${t}` : 'cpt-tag--custom'}`}
                                                >
                                                    {t}
                                                </span>
                                            ))}
                                            {hidden > 0 && <span className="cpt-tag cpt-tag--more">+{hidden}</span>}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
            {state?.nextCursor && (
                <div ref={sentinelRef} className="cpt-sentinel">
                    {state.loadingMore && <div className="cpt-shimmer" />}
                </div>
            )}
        </div>
    );

    return (
        <div className="cpt-wrap">
            <div className="cpt-header">
                <div className="cpt-header-left">
                    <h3 className="cpt-title">Photos</h3>
                    {items.length > 0 && <span className="cpt-count">{items.length}</span>}
                </div>
                <div className="cpt-header-right">
                    {hasConsent && (
                        <button type="button" className="cpt-revoke" onClick={handleRevokeConsent}>
                            Remove consent
                        </button>
                    )}
                    {hasAny && (
                        <button type="button" className="cpt-add-btn" onClick={handleAddClick}>
                            {PLUS_ICON}
                            <span>Add photos</span>
                        </button>
                    )}
                </div>
            </div>

            {loading && !hasAny && (
                <div className="cpt-loading">
                    <div className="cpt-shimmer" />
                </div>
            )}

            {!loading && !hasAny && renderEmpty()}

            {hasAny && (
                <>
                    {pendingUploads.length > 0 && (
                        <div className="cpt-pending-row">
                            {pendingUploads.map((p) => (
                                <div key={p.tempId} className={`cpt-tile cpt-tile--pending ${p.error ? 'cpt-tile--failed' : ''}`}>
                                    <img src={p.thumbUrl} alt="" />
                                    {!p.error && <div className="cpt-tile-spinner" />}
                                    {p.error && <div className="cpt-tile-error">Retry</div>}
                                </div>
                            ))}
                        </div>
                    )}
                    {renderTimeline()}
                </>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFilesPicked}
            />

            {lightboxIndex !== null && items[lightboxIndex] && (
                <ClientPhotoLightbox
                    items={items}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                    onDelete={(photoId) => {
                        handleDeleteFromLightbox(photoId);
                        setLightboxIndex(null);
                    }}
                    onRetag={handleRetag}
                    onViewBooking={handleViewBooking}
                    consent={state?.consent}
                    onShareConsent={handleShareConsent}
                />
            )}

            {consentModalPending && (
                <ClientPhotoConsentModal
                    clientName={clientName}
                    onConfirm={handleConsentConfirmed}
                    onCancel={() => setConsentModalPending(null)}
                />
            )}

            {visibleUndos.length > 0 && (
                <div className="cpt-undo-stack">
                    {visibleUndos.map((u) => (
                        <div key={u.photoId} className="cpt-undo-toast">
                            <span>Photo removed</span>
                            <button type="button" onClick={() => handleUndo(u.photoId)}>Undo</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ClientPhotosTab;
